import { memoize } from 'lodash';
import appPath from '../../../misc/appPath';

/**
 * Resolves an image src to a URL that is known to load, falling back to altSrc if src fails.
 * Results are memoized per (src, altSrc) pair.
 *
 * @param {string} src - Preferred image URL (relative paths are resolved via appPath).
 * @param {string} [altSrc] - Fallback URL used if src is missing or fails to load.
 * @returns {Promise<string>} Resolves to src if it loads, otherwise altSrc.
 */
export const safeImageSrc = memoize(
    (src, altSrc) => {
        // Treat any URL with a scheme (http(s):, blob:, data:, ...) as absolute so blob URLs from
        // URL.createObjectURL aren't mangled by the basename prefix in appPath.
        const resolveAbsolutePath = (s) => (s && !s.match(/^[a-z][a-z\d+.-]*:/i) ? appPath(s) : s),
            resolvedSrc = resolveAbsolutePath(src),
            resolvedAltSrc = resolveAbsolutePath(altSrc);

        if (!resolvedSrc) return Promise.resolve(resolvedAltSrc);

        return new Promise((resolve) => {
            let image = new Image();
            image.onload = () => {
                // console.log('OK for', src, resolvedSrc);
                resolve(resolvedSrc);
            };
            image.onerror = () => {
                // console.log('FAILED for ', src, resolvedSrc, resolvedAltSrc);
                resolve(resolvedAltSrc);
            };
            image.src = resolvedSrc;
        });
    },
    (...args) => args.join()
);
